import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { Combobox } from '../ui/Combobox';
import { AlertTriangle } from 'lucide-react';
import type { Trip } from '../../types/trip';
import type { Vehicle } from '../../types/vehicle';
import type { Driver } from '../../types/driver';
import type { Tariff } from '../../types/finance';
import { getTariffs, type TripCreateDto, type TripUpdateDto } from '../../lib/api/endpoints';
import { tariffFromDto } from '../../lib/api/mappers';
import { aISODate, descripcionTarifa } from '../../utils/finance';
import { fromLocalInputValue, mensajeErrorViaje, toLocalInputValue } from '../../utils/trips';
import { useAuth } from '../../context/AuthContext';
import { hayFinanzas } from '../../utils/features';

interface TripFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Presente = CORREGIR ese viaje. Ausente = programar uno nuevo. */
  trip?: Trip | null;
  vehicles: Vehicle[];
  drivers: Driver[];
  onProgramar: (data: TripCreateDto) => Promise<unknown>;
  /** Obligatorio si se pasa `trip` (modo corrección); quien solo programa puede omitirlo. */
  onCorregir?: (tripId: string, data: TripUpdateDto) => Promise<unknown>;
  /**
   * Solo al PROGRAMAR: prellena la salida (y el fin, una hora después) — lo usa
   * el calendario cuando se hace clic en un día u hora. Nadie está obligado a
   * aceptarlo: son solo los valores iniciales de dos campos editables.
   */
  defaultFrom?: Date | null;
}

interface FormState {
  vehicleId: string;
  driverId: string;
  origin: string;
  destination: string;
  scheduledFrom: string;
  scheduledTo: string;
  actualFrom: string;
  actualTo: string;
  reason: string;
  /** FE-34 — la tarifa con la que se cobra; `''` = sin tarifa. */
  tariffId: string;
}

const VACIO: FormState = {
  vehicleId: '', driverId: '', origin: '', destination: '',
  scheduledFrom: '', scheduledTo: '', actualFrom: '', actualTo: '', reason: '',
  tariffId: '',
};

function desdeTrip(trip: Trip): FormState {
  return {
    vehicleId: trip.vehicleId,
    driverId: trip.driverId ?? '',
    origin: trip.origin,
    destination: trip.destination,
    scheduledFrom: toLocalInputValue(trip.scheduledFrom),
    scheduledTo: toLocalInputValue(trip.scheduledTo),
    actualFrom: toLocalInputValue(trip.actualFrom),
    actualTo: toLocalInputValue(trip.actualTo),
    reason: '',
    tariffId: trip.tariffId ?? '',
  };
}

/**
 * FE-11 — programar un viaje y corregirlo son el MISMO formulario a propósito.
 *
 * Corregir un viaje ya completado no es un caso raro que merezca una pantalla
 * apartada: es la operación central del viaje (ADR-024, PRODUCT.md §4.8). La
 * única diferencia real es qué campos se pueden tocar y que la corrección
 * exige `reason` — la ventana que se mueve reescribe qué alertas, posiciones y
 * gastos pertenecieron al viaje, y eso tiene que quedar auditado.
 *
 * En modo corrección **solo se envía lo que cambió**: mandar el formulario
 * entero haría que cada corrección pareciera tocar las cuatro marcas de tiempo
 * en la novedad, cuando quizá solo se ajustó la hora de llegada.
 */
export function TripFormModal({ isOpen, onClose, trip, vehicles, drivers, onProgramar, onCorregir, defaultFrom }: TripFormModalProps) {
  const esCorreccion = !!trip;
  const [form, setForm] = useState<FormState>(VACIO);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tarifas, setTarifas] = useState<Tariff[]>([]);
  const [cargandoTarifas, setCargandoTarifas] = useState(false);

  // `defaultFrom` se lee por su instante (`getTime`), no por identidad: quien lo
  // pasa suele crear un `Date` nuevo en cada render y el efecto de abajo
  // reiniciaría el formulario a mitad de escritura.
  const defaultFromMs = defaultFrom?.getTime();

  useEffect(() => {
    if (!isOpen) return;
    if (trip) {
      setForm(desdeTrip(trip));
    } else if (defaultFromMs !== undefined && !Number.isNaN(defaultFromMs)) {
      const desde = new Date(defaultFromMs);
      setForm({
        ...VACIO,
        scheduledFrom: toLocalInputValue(desde.toISOString()),
        scheduledTo: toLocalInputValue(new Date(defaultFromMs + 3_600_000).toISOString()),
      });
    } else {
      setForm(VACIO);
    }
    setError(null);
  }, [isOpen, trip, defaultFromMs]);

  const set = (campo: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [campo]: e.target.value }));

  // FE-34 (ADR-032 §1) — la tarifa se elige al programar y se puede cambiar
  // mientras el viaje siga PLANNED; después el contrato la rechaza (409), así
  // que el campo se bloquea en vez de dejar que el servidor lo diga.
  // FE-35 (ADR-034): sin el módulo de finanzas la tarifa no existe: ni el
  // campo ni la llamada a `/tariffs` (que respondería 403 MODULE_DISABLED).
  const { user } = useAuth();
  const conFinanzas = hayFinanzas(user);
  const tarifaEditable = conFinanzas && (!esCorreccion || trip?.status === 'planned');
  const diaProgramado = form.scheduledFrom ? aISODate(new Date(form.scheduledFrom)) : undefined;
  const claseVehiculo = vehicles.find(v => v.id === form.vehicleId)?.vehicleClass;

  // Las tarifas vigentes el día de la salida programada, de la clase del
  // vehículo (o sin clase), de cualquier cliente: el cliente se DERIVA de la
  // tarifa, no se elige aparte (ADR-032 §1). El filtro de vigencia es del
  // servidor (`validAt`); el de clase se aplica en memoria para no volver a
  // pedir la lista cada vez que se cambia de vehículo (cabe entera).
  useEffect(() => {
    if (!isOpen || !tarifaEditable || !diaProgramado) { setTarifas([]); return; }
    let cancelled = false;
    setCargandoTarifas(true);
    getTariffs({ validAt: diaProgramado })
      .then(res => { if (!cancelled) setTarifas(res.items.map(tariffFromDto)); })
      .catch(() => { if (!cancelled) setTarifas([]); })
      .finally(() => { if (!cancelled) setCargandoTarifas(false); });
    return () => { cancelled = true; };
  }, [isOpen, tarifaEditable, diaProgramado]);

  const tarifasAplicables = tarifas.filter(t => t.active && (!t.vehicleClass || !claseVehiculo || t.vehicleClass === claseVehiculo));
  // La elegida puede no estar en la lista (al corregir, si la vigencia cambió
  // o el vehículo es de otra clase): se muestra igual, con su nombre.
  const tarifaElegida = tarifas.find(t => t.id === form.tariffId);
  const opcionesTarifa = tarifasAplicables.map(t => ({ value: t.id, label: `${t.customerName} · ${t.name}`, hint: descripcionTarifa(t) }));
  if (form.tariffId && !tarifasAplicables.some(t => t.id === form.tariffId)) {
    opcionesTarifa.unshift({ value: form.tariffId, label: tarifaElegida ? `${tarifaElegida.customerName} · ${tarifaElegida.name}` : (trip?.tariffName ?? 'Tarifa actual'), hint: tarifaElegida ? descripcionTarifa(tarifaElegida) : 'no vigente para esta fecha o clase de vehículo' });
  }

  const validar = (): string | null => {
    if (!esCorreccion && !form.vehicleId) return 'Elige el vehículo del viaje.';
    if (!form.origin.trim()) return 'El origen es obligatorio.';
    if (!form.destination.trim()) return 'El destino es obligatorio.';
    if (!form.scheduledFrom || !form.scheduledTo) return 'La franja programada necesita inicio y fin.';
    if (Date.parse(form.scheduledTo) <= Date.parse(form.scheduledFrom)) {
      return 'El fin programado tiene que ser posterior al inicio.';
    }
    if (form.actualFrom && form.actualTo && Date.parse(form.actualTo) <= Date.parse(form.actualFrom)) {
      return 'La llegada real no puede ser anterior a la salida real.';
    }
    if (esCorreccion && !form.reason.trim()) return 'La razón de la corrección es obligatoria — queda en la novedad.';
    return null;
  };

  const enviar = async () => {
    const problema = validar();
    if (problema) { setError(problema); return; }

    setEnviando(true);
    setError(null);
    try {
      if (esCorreccion && trip) {
        // Solo los campos que cambiaron, más la razón.
        const original = desdeTrip(trip);
        const cambios: TripUpdateDto = { reason: form.reason.trim() };
        if (form.origin !== original.origin) cambios.origin = form.origin.trim();
        if (form.destination !== original.destination) cambios.destination = form.destination.trim();
        if (form.driverId !== original.driverId) cambios.driverId = form.driverId || null;
        if (form.scheduledFrom !== original.scheduledFrom) cambios.scheduledFrom = fromLocalInputValue(form.scheduledFrom);
        if (form.scheduledTo !== original.scheduledTo) cambios.scheduledTo = fromLocalInputValue(form.scheduledTo);
        if (form.actualFrom !== original.actualFrom) cambios.actualFrom = fromLocalInputValue(form.actualFrom) ?? null;
        if (form.actualTo !== original.actualTo) cambios.actualTo = fromLocalInputValue(form.actualTo) ?? null;
        if (form.tariffId !== original.tariffId) {
          if (form.tariffId) cambios.tariffId = form.tariffId; else cambios.unset = ['tariffId'];
        }

        if (Object.keys(cambios).length === 1) {
          setError('No cambiaste ningún dato del viaje.');
          setEnviando(false);
          return;
        }
        // Un fallo de quien monta el modal, no del usuario: mejor ruidoso que
        // un "Guardar" que no hace nada.
        if (!onCorregir) throw new Error('TripFormModal recibió un viaje para corregir pero no `onCorregir`.');
        await onCorregir(trip.id, cambios);
      } else {
        await onProgramar({
          vehicleId: form.vehicleId,
          driverId: form.driverId || undefined,
          origin: form.origin.trim(),
          destination: form.destination.trim(),
          scheduledFrom: fromLocalInputValue(form.scheduledFrom)!,
          scheduledTo: fromLocalInputValue(form.scheduledTo)!,
          tariffId: form.tariffId || undefined,
        });
      }
      onClose();
    } catch (err) {
      setError(mensajeErrorViaje(err, esCorreccion ? 'corregir' : 'programar'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={esCorreccion ? `Corregir viaje · ${trip?.vehiclePlate}` : 'Programar viaje'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={enviar} isLoading={enviando}>
            {esCorreccion ? 'Guardar corrección' : 'Programar'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        {esCorreccion && trip?.status === 'completed' && (
          <div style={{ display: 'flex', gap: '0.625rem', padding: '0.75rem 0.875rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '12px' }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>
              Este viaje ya está completado. Mover sus horas <strong>reescribe qué alertas y qué
              recorrido le pertenecieron</strong>: el expediente se deriva de la ventana, no está
              estampado. La corrección queda registrada en la novedad.
            </span>
          </div>
        )}

        <Select
          label="Vehículo"
          value={form.vehicleId}
          onChange={set('vehicleId')}
          // El vehículo no se cambia al corregir: mover un viaje de vehículo
          // sería otro viaje, y el contrato no lo admite en `TripUpdate`.
          disabled={esCorreccion}
          options={[
            { value: '', label: 'Selecciona un vehículo…' },
            ...vehicles.map(v => ({ value: v.id, label: `${v.plate}${v.model ? ` · ${v.model}` : ''}` })),
          ]}
        />

        <Select
          label="Conductor (opcional)"
          value={form.driverId}
          onChange={set('driverId')}
          options={[
            { value: '', label: 'Sin conductor asignado' },
            ...drivers.map(d => ({ value: d.id, label: d.name })),
          ]}
        />

        <Input label="Origen" value={form.origin} onChange={set('origin')} placeholder="Terminal Salitre, Bogotá" />
        <Input label="Destino" value={form.destination} onChange={set('destination')} placeholder="Zona Industrial, Zipaquirá" />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
          <Input label="Salida programada" type="datetime-local" value={form.scheduledFrom} onChange={set('scheduledFrom')} />
          <Input label="Llegada programada" type="datetime-local" value={form.scheduledTo} onChange={set('scheduledTo')} />
        </div>

        {/* FE-34 — la tarifa va DESPUÉS de las fechas a propósito: la lista
            depende de la salida programada (vigencia) y del vehículo (clase). */}
        {conFinanzas && (<>
        <Combobox
          label="Tarifa (opcional)"
          value={form.tariffId}
          onChange={v => setForm(f => ({ ...f, tariffId: v }))}
          disabled={!tarifaEditable || cargandoTarifas || (!form.tariffId && !diaProgramado)}
          placeholder={!diaProgramado ? 'Pon primero la salida programada' : cargandoTarifas ? 'Cargando tarifas…' : 'Busca por cliente, ruta o servicio…'}
          emptyLabel="Sin tarifa (el viaje no genera ingreso)"
          options={opcionesTarifa}
        />
        {tarifaEditable ? (
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
            {form.tariffId && tarifaElegida
              ? <>Cliente <strong>{tarifaElegida.customerName}</strong> · {descripcionTarifa(tarifaElegida)}. Al iniciar, el conductor declara el peso del manifiesto y el ingreso se crea solo.</>
              : diaProgramado && !cargandoTarifas && tarifasAplicables.length === 0
                ? <>No hay tarifas vigentes el {diaProgramado}{claseVehiculo ? ` para vehículos ${claseVehiculo}` : ''}. Sin tarifa, el ingreso se registra a mano.</>
                : <>Con tarifa, el ingreso del viaje se crea al iniciarlo con el peso que declare el conductor (ADR-032).</>}
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
            La tarifa solo se cambia mientras el viaje está programado. {trip?.revenueId ? 'El ingreso ya existe: ajústalo desde Ingresos con «Corregir».' : ''}
          </p>
        )}
        </>)}

        {esCorreccion && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
              <Input label="Salida real" type="datetime-local" value={form.actualFrom} onChange={set('actualFrom')} />
              <Input label="Llegada real" type="datetime-local" value={form.actualTo} onChange={set('actualTo')} />
            </div>
            <Textarea
              label="Razón de la corrección (obligatoria)"
              value={form.reason}
              onChange={set('reason')}
              rows={3}
              placeholder="El conductor reportó que salió 40 minutos tarde por el cierre de la vía."
            />
          </>
        )}

        {!esCorreccion && (
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
            La llegada programada es obligatoria: el viaje se cierra solo a esa hora, sin depender
            de que alguien lo finalice. Si el vehículo ya tiene otro viaje en esa franja, el
            servidor lo rechaza.
          </p>
        )}

        {error && (
          <p style={{ margin: 0, padding: '0.625rem 0.875rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)', color: '#EF4444', fontSize: '12px', fontWeight: 600 }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
